import { Injectable } from '@angular/core';
import { BehaviorSubject, concat } from 'rxjs';
import { User } from './../models/user.model';
import { CindiMessage } from './../models/cindi-message.model';
import { UserAnswer } from './../models/user-answer.model';
import { UserQuestionnaire } from './../models/user-questionnaire.model';
import { Alternative } from './../models/alternative.model';
import { CindiService } from './cindi.service';
import axiosClient from './axios-client';

@Injectable({
  providedIn: 'root',
})
export class AnswerService {
  public user: User = new User();
  public hasIntensePain: boolean = true;
  private userQuestionnaire: UserQuestionnaire = new UserQuestionnaire();
  private allUserAnswers: UserAnswer[] = [];

  constructor(private cindiService: CindiService) {}

  private progressBarCountSubject = new BehaviorSubject<number>(5);
  progressBarCount$ = this.progressBarCountSubject.asObservable();

  get currentProgressBarCount(): number {
    return this.progressBarCountSubject.value;
  }

  private previousQuestionIdSubject = new BehaviorSubject<number | null>(null);
  previousQuestionId$ = this.previousQuestionIdSubject.asObservable();

  get previousQuestionId(): number | null {
    return this.previousQuestionIdSubject.value;
  }

  private continueQuestionsFlowSubject = new BehaviorSubject<boolean>(false);
  continueQuestionsFlow$ = this.continueQuestionsFlowSubject.asObservable();

  get continueQuestionsFlow(): boolean {
    return this.continueQuestionsFlowSubject.value;
  }

  private hasOtherSymptomsSubject = new BehaviorSubject<boolean>(false);
  hasOtherSymptoms$ = this.hasOtherSymptomsSubject.value;

  get hasOtherSymptoms(): boolean {
    return this.hasOtherSymptomsSubject.value;
  }

  private availableToChangesSubject = new BehaviorSubject<boolean>(false);
  availableToChanges$ = this.availableToChangesSubject.value;

  get availableToChanges(): boolean {
    return this.availableToChangesSubject.value;
  }

  setHasOtherSymptoms(value: boolean): void {
    this.hasOtherSymptomsSubject.next(value);
  }

  setavailableToChanges(value: boolean): void {
    this.availableToChangesSubject.next(value);
  }

  setContinueQuestionsFlow(value: boolean): void {
    this.continueQuestionsFlowSubject.next(value);
  }

  setProgressBarProgressPositive(value: number) {
    let currentBarValue = this.progressBarCountSubject.value;
    this.progressBarCountSubject.next(currentBarValue + value);
  }

  setPreviousQuestionId(id: number) {
    this.previousQuestionIdSubject.next(id);
  }

  private personalDataSubject = new BehaviorSubject<any>({
    pais: null,
    cidadeResidencia: null,
    estadoCivil: null,
    grauEscolaridade: null,
    classificacaoEtnicoRacial: null,
    cargo: null,
    area: null,
    turno: null,
  });

  personalData$ = this.personalDataSubject.asObservable();

  get personalData() {
    return this.personalDataSubject.value;
  }

  setPersonalData(data: any) {
    this.personalDataSubject.next({
      ...this.personalData,
      ...data,
    });
  }

  setPersonalField(field: string, value: any) {
    this.personalDataSubject.next({
      ...this.personalData,
      [field]: value,
    });
  }

  private getDynamicProgressIncrement(): number {
    const fields = ['pais', 'cidadeResidencia', 'estadoCivil', 'grauEscolaridade', 'classificacaoEtnicoRacial', 'cargo', 'area', 'turno'];

    const emptyFieldsCount = fields.filter(field =>
      this.personalData[field] === null || this.personalData[field] === ''
    ).length;

    if (emptyFieldsCount >= 6) return 2;
    if (emptyFieldsCount >= 3) return 3;
    return 5;
  }

  async setAnswer(question: CindiMessage, index: number) {
    const selectedAlternative = question.alternatives[index];
    const isPersonalData = (question.id >= 9 && question.id <= 16) || (question.id >= 54 && question.id <= 55);
    const increment = isPersonalData ? this.getDynamicProgressIncrement() : 5;

    this.setProgressBarProgressPositive(increment);

    await this.disablePreviousOptionsAndMessages();

    const userAnswer: UserAnswer = new UserAnswer(
      this.userQuestionnaire.pessoaQuestionarioId,
      question.id,
      question.tagMensagem,
      [selectedAlternative],
      null
    );

    this.allUserAnswers.push(userAnswer)

    if (question.id >= 64 && question.id <= 77) {
      this.cindiService.addPontuation(selectedAlternative.pontuacao!);
      this.previousQuestionIdSubject.next(question.id);
      this.setContinueQuestionsFlow(true);
    } else if (question.tagMensagem === 'Outros sintomas') {
      this.setHasOtherSymptomsFlow(selectedAlternative.id);
      this.previousQuestionIdSubject.next(question.id);
      this.setContinueQuestionsFlow(true);
    } else if (isPersonalData) {
      const fieldsMap: any = {
        9: 'pais',
        10: 'cidadeResidencia',
        11: 'estadoCivil',
        12: 'grauEscolaridade',
        13: 'classificacaoEtnicoRacial',
        54: 'cargo',
        55: 'area',
        16: 'turno'
      };

      const field = fieldsMap[question.id];

      if (selectedAlternative?.texto) {
        this.setPersonalField(field, selectedAlternative.texto);
      }
      this.previousQuestionIdSubject.next(question.id);
      this.setContinueQuestionsFlow(true);
    } else if (question.id == 91) {
      this.cindiService.addPontuation(selectedAlternative.pontuacao!);
      selectedAlternative.pontuacao == 2 ?
      this.setavailableToChanges(true) : this.setavailableToChanges(false);
      this.previousQuestionIdSubject.next(question.id);
      this.setContinueQuestionsFlow(true);
    } else {
      this.previousQuestionIdSubject.next(question.id);
      this.setContinueQuestionsFlow(true);
    }
  }

  async setMultipleAnswers(
    question: CindiMessage,
    alternativesArray: Alternative[]
  ) {
    const isPersonalData = (question.id >= 9 && question.id <= 16);
    const increment = isPersonalData ? this.getDynamicProgressIncrement() : 5;

    this.setProgressBarProgressPositive(increment);

    if (alternativesArray.length > 0) {
      const userAnswer: UserAnswer = new UserAnswer(
        this.userQuestionnaire.pessoaQuestionarioId,
        question.id,
        question.tagMensagem,
        alternativesArray,
        null
      );

      const fieldsMap: any = {
        9: 'pais',
        10: 'cidadeResidencia',
        11: 'estadoCivil',
        12: 'grauEscolaridade',
        13: 'classificacaoEtnicoRacial',
        54: 'cargo',
        55: 'area',
        16: 'turno'
      };

      const field = fieldsMap[question.id];

      if (field) {
        const selectedTexts = alternativesArray
          .map(a => a.texto)
          .filter(t => t && t.trim() !== '')
          .join(', ');

        this.setPersonalField(field, selectedTexts);
      }

      this.allUserAnswers.push(userAnswer);
      this.previousQuestionIdSubject.next(question.id);
      this.setContinueQuestionsFlow(true);
    }
  }

  async setOpenAnswer(question: CindiMessage, inputText: string) {
    const isPersonalData = (question.id >= 9 && question.id <= 16);
    const increment = isPersonalData ? this.getDynamicProgressIncrement() : 5;

    this.setProgressBarProgressPositive(increment);
    await this.disablePreviousOptionsAndMessages();

    if ('Peso'.includes(question.tagMensagem)) {
      let float = inputText.replace(',', '.')
      this.cindiService.weigth = parseFloat(float)
    }

    if ('Altura'.includes(question.tagMensagem)) {
      let float = inputText.replace(',', '.')
      this.cindiService.heigth = parseFloat(float)
      this.cindiService.calculateIMCscore()
    }

    const userAnswer: UserAnswer = new UserAnswer(
      this.userQuestionnaire.pessoaQuestionarioId,
      question.id,
      question.tagMensagem,
      null,
      inputText
    );

    if (question.id >= 9 && question.id <= 16) {
      const fieldsMap: any = {
        9: 'pais',
        10: 'cidadeResidencia'
      };
      const field = fieldsMap[question.id];
      this.setPersonalField(field, inputText);
    }

    if (inputText == '') return;
    this.allUserAnswers.push(userAnswer);

    this.previousQuestionIdSubject.next(question.id);
    this.setContinueQuestionsFlow(true);

  }

  async disablePreviousOptionsAndMessages() {
    document.querySelectorAll('.help-text').forEach((p) => p.remove());

    document.querySelectorAll('.rate-scale-img').forEach((e) => {
      e.classList.add('lowlight');
    });

    document.querySelectorAll('.answer-option').forEach((btn) => {
      if (
        btn instanceof HTMLButtonElement &&
        !btn.classList.contains('checkbox-option') &&
        !btn.classList.contains('send-btn')
      ) {
        btn.classList.add('option-unselected-disabled');
      }
    });

    document.querySelectorAll('.answer-option.lowlight').forEach((btn) => {
      if (
        btn instanceof HTMLButtonElement &&
        !btn.classList.contains('checkbox-option') &&
        !btn.classList.contains('send-btn')
      ) {
        btn.classList.remove('lowlight', 'option-unselected-disabled');
        btn.classList.add('option-selected-disabled');
      }
    });

    document.querySelectorAll('.bot-msg').forEach((p) => {
      p.classList.add('msg-disabled');
    });
  }

  setHasOtherSymptomsFlow(alternative: number) {
    if (alternative === 199) this.setHasOtherSymptoms(true);
    else if (alternative === 198) this.setHasOtherSymptoms(false);
  }

  async createAnonymousUserQuestionnaire() {
    return await axiosClient.post('api/cindi/user-questionnaires/2', {}).then((res: any) => {
      this.userQuestionnaire = res.data.data;
    });
  }

  async createUserQuestionnaire(idPessoa: number) {
    return await axiosClient.post(`api/cindi/user-questionnaires/2/${idPessoa}`, {}).then((res: any) => {
      this.userQuestionnaire = res.data.data;
    });
  }

  async saveUserAnswers(questionnaireAnswers: UserAnswer[]) {
    const filteredAnswers = questionnaireAnswers.filter(
      (answer) => answer.questionTag !== 'Decisao do fluxo'
    );
    await axiosClient.post('api/cindi/user-answers', filteredAnswers);
  }

  async saveAnswersByQuestionnaire() {
    await this.saveUserAnswers(this.allUserAnswers);
  }

  async setUserQuestionnaireStatus(status: string) {
    this.userQuestionnaire.status = status;

    await axiosClient.put('api/cindi/user-questionnaires', this.userQuestionnaire);
  }

  async loadPersonalDataFromApi(userId: number) {
    try {
      const apiResponse = await axiosClient.get('api/pessoa/' + userId);
      const data = apiResponse.data;

      this.personalDataSubject.next({
        ...this.personalData,
        ...data
      });

      return data;
    } catch (e) {
      console.error("Erro ao buscar dados pessoais", e);
      return null;
    }
  }
}
